import { Img } from "components";
import Account from "./Account";
import DeliveryMode from "./DeliveryMode";
import Payment from "./Payment";
import DeliveryDateTime from "./DeliveryDateTime";
import { getOrderData } from "../../../redux/reducers/orderData";
import { useSelector, useDispatch } from "react-redux";
import { updateOrderData } from "../../../redux/reducers/orderData";
import { useState, useEffect } from "react";

// Step IDs matching Cart's STEPS pattern
const STEPS = {
  ACCOUNT: 1,
  DELIVERY: 2,
  DATETIME: 3,
  PAYMENT: 4,
};

const CartProductStep = ({
  addressList,
  zoneList,
  occasionOptions,
  orderDetails,
  orderSummary,
  isEventSent,
  setIsEventSent,
  sendCheckoutGaEvent,
  onViewOrderSummary,
}) => {
  const orderData = useSelector(getOrderData);
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(STEPS.ACCOUNT);

  // Sync activeStep with orderData.pageStep — mirrors Cart's CartProductStep logic
  useEffect(() => {
    if (orderData?.pageStep === 4) {
      setActiveStep(STEPS.PAYMENT);
    } else if (orderData?.pageStep === 3) {
      setActiveStep(STEPS.DATETIME);
    } else if (orderData?.delivery_mode) {
      setActiveStep(STEPS.DATETIME);
    } else if (orderData?.pageStep === 2) {
      setActiveStep(STEPS.DELIVERY);
    } else {
      setActiveStep(STEPS.ACCOUNT);
    }
  }, [orderData?.delivery_mode, orderData?.pageStep]);

  // Determine if a step is completed — mirrors Cart logic
  const isCompleted = (stepId) => {
    switch (stepId) {
      case STEPS.ACCOUNT:
        // CustomCake: account is always pre-filled (user must be logged in for custom cake)
        return !!orderData?.userDetails?.first_name;
      case STEPS.DELIVERY:
        return !!orderData?.delivery_mode;
      case STEPS.DATETIME:
        if (orderData?.delivery_mode === "buying_for_love") {
          return orderData?.pageStep >= 4;
        }
        return orderData?.pageStep >= 4 && orderData?.deliveryDateTimeActiveTab !== undefined;
      case STEPS.PAYMENT:
        return false;
      default:
        return false;
    }
  };

  // Determine step visibility — mirrors Cart's isStepVisible logic
  const isStepVisible = (step) => {
    switch (step) {
      case STEPS.ACCOUNT:
        return orderData?.pageStep <= 1 || !orderData?.userDetails?.first_name;

      case STEPS.DELIVERY:
        return (
          !!orderData?.userDetails?.first_name &&
          orderData?.pageStep === 2
        );

      case STEPS.DATETIME:
        return (
          !!orderData?.userDetails?.first_name &&
          !!orderData?.delivery_mode &&
          orderData?.delivery_mode !== "buying_for_love" &&
          orderData?.pageStep === 3
        );

      case STEPS.PAYMENT:
        return (
          !!orderData?.userDetails?.first_name &&
          !!orderData?.delivery_mode &&
          orderData?.pageStep === 4
        );

      default:
        return false;
    }
  };

  // Current step meta — mirrors Cart's getCurrentStepInfo
  const getCurrentStepInfo = () => {
    const steps = {
      [STEPS.ACCOUNT]: { name: "Account", icon: "images/person.svg", activeIcon: "images/check.svg" },
      [STEPS.DELIVERY]: { name: "Delivery", icon: "images/mapLocation.png", activeIcon: "images/mapLocation.png" },
      [STEPS.DATETIME]: { name: "Date & Time", icon: "images/dateTime.png", activeIcon: "images/dateTime.png" },
      [STEPS.PAYMENT]: { name: "Payment", icon: "images/cartPayment.png", activeIcon: "images/cartPayment.png" },
    };
    const current = steps[activeStep] || steps[STEPS.ACCOUNT];
    const isCompletedStep = activeStep === STEPS.ACCOUNT && !!orderData?.userDetails?.first_name;
    return { ...current, displayIcon: isCompletedStep ? current.activeIcon : current.icon };
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-y-8 ltr:md:pl-7 rtl:md:pr-7">
      {/* SINGLE CART CARD — same structure as Cart's CartProductStep */}
      <div className="bg-white-A700 rounded-md shadow-sm overflow-hidden">

        {/* ── Mobile header (step name + View Order Summary) ── */}
        <div className="hidden sm:block px-6 sm:px-3 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-200 border-2 rounded-full flex-shrink-0">
                <Img
                  src={getCurrentStepInfo().displayIcon}
                  alt={getCurrentStepInfo().name}
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[#1A1A1A] font-[Unbounded] text-[12px] font-normal leading-[16px]">
                  {getCurrentStepInfo().name}
                </span>
                <span className="text-[#6D737E] font-[Inter] text-[12px] font-normal leading-[16px]">
                  Step {activeStep} of 4
                </span>
              </div>
            </div>
            <button
              onClick={() => onViewOrderSummary && onViewOrderSummary()}
              className="flex items-center gap-2 text-[#1A1A1A] font-['Inter'] text-[12px] font-normal leading-[16px] flex-shrink-0"
            >
              <span>View Order Summary</span>
              <Img src="/images/DownArrow.svg" alt="arrow" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Desktop top stepper — hidden on mobile (<640px) ── */}
        <div className="block sm:hidden px-6 py-4 border-b border-gray-200">
          <div className="relative flex items-center justify-between">
            {/* background progress line */}
            <div className="absolute top-[28px] left-6 right-6 h-[2px] bg-gray-200" />
            {/* active progress line */}
            <div
              className="absolute top-[28px] left-6 h-[2px] bg-[#771237] transition-all duration-300"
              style={{
                width:
                  activeStep === STEPS.ACCOUNT ? "0%"
                  : activeStep === STEPS.DELIVERY ? "33%"
                  : activeStep === STEPS.DATETIME ? "66%"
                  : "100%",
              }}
            />

            {[
              { id: STEPS.ACCOUNT,  label: "Account",    icon: "/images/check.svg" },
              { id: STEPS.DELIVERY, label: "Delivery",   icon: "/images/mapLocation.png" },
              { id: STEPS.DATETIME, label: "Date & Time",icon: "/images/dateTime.png" },
              { id: STEPS.PAYMENT,  label: "Payment",    icon: "/images/cartPayment.png" },
            ].map((step) => {
              const completed = isCompleted(step.id);
              const isActiveOrDone = completed || activeStep === step.id;

              return (
                <div
                  key={step.id}
                  onClick={() => {
                    if (completed || step.id === activeStep) {
                      setActiveStep(step.id);
                      let newPageStep = step.id;
                      if (step.id === STEPS.ACCOUNT) {
                        newPageStep = 1;
                      } else if (step.id === STEPS.DELIVERY) {
                        newPageStep = 2;
                      } else if (step.id === STEPS.DATETIME) {
                        newPageStep = orderData?.delivery_mode ? 3 : 2;
                        if (!orderData?.delivery_mode) setActiveStep(STEPS.DELIVERY);
                      } else if (step.id === STEPS.PAYMENT) {
                        newPageStep = 4;
                      }
                      dispatch(updateOrderData({ ...orderData, pageStep: newPageStep }));
                    }
                  }}
                  className={`relative z-10 flex flex-col items-center transition-opacity
                    ${isActiveOrDone || completed ? "cursor-pointer hover:opacity-80" : "cursor-not-allowed opacity-60"}`}
                >
                  <div
                    className={`flex items-center justify-center rounded-full transition-all
                      ${isActiveOrDone ? "bg-[#771237]" : "bg-[white] border border-gray-300"}`}
                    style={{
                      width:  isActiveOrDone ? "56px" : "48px",
                      height: isActiveOrDone ? "56px" : "48px",
                      padding: isActiveOrDone ? "11.2px" : "8px",
                    }}
                  >
                    <Img
                      src={completed ? "/images/check.svg" : step.icon}
                      alt={step.label}
                      className="w-[32px] h-[32px]"
                    />
                  </div>
                  <span className={`mt-2 text-xs font-medium font-['Inter'] ${isActiveOrDone ? "text-[#771237]" : "text-gray-400"}`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Account Step ── */}
        {isStepVisible(STEPS.ACCOUNT) && (
          <div className="relative bg-white-A700 py-[18px] px-6 xs:px-4">
            {/* step-line kept for layout parity; step icons commented out as in Cart */}
            {/* <div className="absolute bg-red-100 flex h-9 items-center justify-center -left-7 p-1.5 top-6 w-9 z-[1] step-icon active">
              <Img className="h-6" src="/images/person.svg" alt="user" />
            </div> */}
            <div className="absolute border-l-2 mt-2 border-gray-300 border-dashed w-px -left-3 rtl:-left-[auto] rtl:-right-3 z-[0] step-line success"></div>
            <Account />
          </div>
        )}

        {/* ── Delivery Mode Step ── */}
        {isStepVisible(STEPS.DELIVERY) && (
          <div className="relative bg-white-A700 py-[18px] px-6 xs:px-4">
            {/* <div className="absolute bg-white-A700 flex h-9 items-center justify-center -left-7 p-1.5 top-6 shadow-bs6 w-9 z-[1] step-icon active">
              <Img className="h-6" src="/images/location_on.svg" alt="location" />
            </div> */}
            <div className="absolute border-l-2 mt-2 border-gray-300 border-dashed w-px -left-3 rtl:-left-[auto] rtl:-right-3 z-[0] step-line"></div>
            <DeliveryMode
              addressList={addressList}
              zoneList={zoneList}
              occasionOptions={occasionOptions}
              isEventSent={isEventSent}
              setIsEventSent={setIsEventSent}
              sendCheckoutGaEvent={sendCheckoutGaEvent}
            />
          </div>
        )}

        {/* ── Delivery Date & Time Step (hidden for buying_for_love) ── */}
        {isStepVisible(STEPS.DATETIME) && orderData?.delivery_mode !== "buying_for_love" && (
          <div className="relative bg-white-A700 py-[18px] px-6 xs:px-4">
            {/* <div className="absolute bg-white-A700 flex h-9 items-center justify-center -left-7 p-1.5 top-6 shadow-bs6 w-9 z-[1] step-icon">
              <Img className="h-6" src="/images/event_note.svg" alt="calendar" />
            </div> */}
            <div className="absolute border-l-2 mt-2 border-gray-300 border-dashed w-px -left-3 rtl:-left-[auto] rtl:-right-3 z-[0] step-line"></div>
            <DeliveryDateTime orderDetails={orderDetails} />
          </div>
        )}

        {/* ── Payment Step ── */}
        {isStepVisible(STEPS.PAYMENT) && (
          <div className="relative bg-white-A700 pt-[18px]">
            {/* <div className="absolute bg-white-A700 flex h-9 items-center justify-center -left-7 p-1.5 top-6 shadow-bs6 w-9 z-[1] step-icon">
              <Img className="h-6" src="/images/account_balance_wallet.svg" alt="camera" />
            </div> */}
            <Payment
              orderSummary={orderSummary}
              orderDetails={orderDetails}
              isEventSent={isEventSent}
              setIsEventSent={setIsEventSent}
              sendCheckoutGaEvent={sendCheckoutGaEvent}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartProductStep;
